import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/lib/supabase';
import { Database, RefreshCw, CheckCircle2, XCircle, AlertTriangle, TrendingUp } from 'lucide-react';

interface CompanyStats {
  company_id: string;
  company_name: string;
  existing_count: number;
  successware_total: number;
  missing_count: number;
  status: 'idle' | 'checking' | 'importing' | 'complete' | 'error';
  imported?: number;
  failed?: number;
  error_message?: string;
}

export default function SuccessWareTrueUpPage() {
  const [companies, setCompanies] = useState<CompanyStats[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);

  // Analyze what's missing across all companies
  const analyzeGaps = async () => {
    setIsAnalyzing(true);
    try {
      // Get active companies
      const { data: companiesData, error: companiesError } = await supabase
        .from('sw_companies')
        .select('id, name')
        .eq('is_active', true);

      if (companiesError) throw companiesError;

      const stats: CompanyStats[] = [];

      for (const company of companiesData || []) {
        setCompanies(prev => [...prev.filter(c => c.company_id !== company.id), {
          company_id: company.id,
          company_name: company.name,
          existing_count: 0,
          successware_total: 0,
          missing_count: 0,
          status: 'checking' as const
        }]);

        try {
          // Count existing customers in our database
          const { count: existingCount, error: countError } = await supabase
            .from('customers')
            .select('*', { count: 'exact', head: true })
            .eq('created_by_company_id', company.id);

          if (countError) throw countError;

          // Get total from SuccessWare API (using first page metadata)
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/successware-import`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              company_id: company.id,
              preview_mode: true,
              limit_per_company: 1
            })
          });

          const result = await response.json();
          const swTotal = result.preview_data?.[0]?.customers?.[0]?.api_data?.searchServiceAccounts?.totalElements || 0;

          const stat: CompanyStats = {
            company_id: company.id,
            company_name: company.name,
            existing_count: existingCount || 0,
            successware_total: swTotal,
            missing_count: Math.max(0, swTotal - (existingCount || 0)),
            status: 'idle'
          };

          stats.push(stat);
          setCompanies(prev => [...prev.filter(c => c.company_id !== company.id), stat]);

        } catch (error: any) {
          const errorStat: CompanyStats = {
            company_id: company.id,
            company_name: company.name,
            existing_count: 0,
            successware_total: 0,
            missing_count: 0,
            status: 'error',
            error_message: error.message
          };
          stats.push(errorStat);
          setCompanies(prev => [...prev.filter(c => c.company_id !== company.id), errorStat]);
        }
      }

    } catch (error: any) {
      console.error('Analysis failed:', error);
      alert(`Analysis failed: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Import missing customers for a specific company
  const importMissingForCompany = async (companyId: string) => {
    const companyIndex = companies.findIndex(c => c.company_id === companyId);
    if (companyIndex === -1) return;

    // Update status to importing
    setCompanies(prev => {
      const updated = [...prev];
      updated[companyIndex].status = 'importing';
      return updated;
    });

    try {
      // Call the true-up edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/successware-true-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          company_id: companyId,
          batch_size: 100
        })
      });

      const result = await response.json();

      if (result.success) {
        setCompanies(prev => {
          const updated = [...prev];
          updated[companyIndex].status = 'complete';
          updated[companyIndex].imported = result.records_imported || 0;
          updated[companyIndex].failed = result.records_failed || 0;
          updated[companyIndex].existing_count += result.records_imported || 0;
          updated[companyIndex].missing_count = Math.max(0, updated[companyIndex].missing_count - (result.records_imported || 0));
          return updated;
        });
      } else {
        throw new Error(result.error || 'Import failed');
      }

    } catch (error: any) {
      setCompanies(prev => {
        const updated = [...prev];
        updated[companyIndex].status = 'error';
        updated[companyIndex].error_message = error.message;
        return updated;
      });
    }
  };

  // Import all missing customers across all companies
  const importAllMissing = async () => {
    setIsImporting(true);
    const companiesWithGaps = companies.filter(c => c.missing_count > 0 && c.status !== 'error');
    
    for (let i = 0; i < companiesWithGaps.length; i++) {
      await importMissingForCompany(companiesWithGaps[i].company_id);
      setOverallProgress(((i + 1) / companiesWithGaps.length) * 100);
    }
    
    setIsImporting(false);
    setOverallProgress(0);
  };

  const totalMissing = companies.reduce((sum, c) => sum + c.missing_count, 0);
  const totalExisting = companies.reduce((sum, c) => sum + c.existing_count, 0);
  const totalSuccessWare = companies.reduce((sum, c) => sum + c.successware_total, 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SuccessWare True-Up</h1>
          <p className="text-muted-foreground mt-1">
            Identify and import missing customer records from SuccessWare
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={analyzeGaps}
            disabled={isAnalyzing || isImporting}
            variant="outline"
          >
            <Database className="w-4 h-4 mr-2" />
            {isAnalyzing ? 'Analyzing...' : 'Analyze Gaps'}
          </Button>
          {companies.length > 0 && totalMissing > 0 && (
            <Button
              onClick={importAllMissing}
              disabled={isImporting || isAnalyzing}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {isImporting ? 'Importing...' : `Import All Missing (${totalMissing})`}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {companies.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                SuccessWare Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalSuccessWare.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Total customers in SuccessWare
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Currently Imported
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalExisting.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalSuccessWare > 0 ? `${Math.round((totalExisting / totalSuccessWare) * 100)}%` : '0%'} of total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Missing Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-500">
                {totalMissing.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Records to import
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Overall Progress */}
      {isImporting && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Overall Import Progress</span>
                <span>{Math.round(overallProgress)}%</span>
              </div>
              <Progress value={overallProgress} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company-by-Company Breakdown */}
      {companies.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold">Company Breakdown</h2>
          {companies.map((company) => (
            <Card key={company.company_id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{company.company_name}</CardTitle>
                    <CardDescription>
                      {company.status === 'checking' && 'Analyzing...'}
                      {company.status === 'idle' && `${company.missing_count.toLocaleString()} missing records`}
                      {company.status === 'importing' && 'Importing...'}
                      {company.status === 'complete' && 'Import complete'}
                      {company.status === 'error' && `Error: ${company.error_message}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    {company.status === 'checking' && (
                      <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                    )}
                    {company.status === 'idle' && company.missing_count > 0 && (
                      <Button
                        size="sm"
                        onClick={() => importMissingForCompany(company.company_id)}
                        disabled={isImporting}
                      >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Import {company.missing_count}
                      </Button>
                    )}
                    {company.status === 'importing' && (
                      <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                    )}
                    {company.status === 'complete' && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                    {company.status === 'error' && (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
              </CardHeader>
              {company.status !== 'checking' && (
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">SuccessWare Total</div>
                      <div className="text-2xl font-semibold mt-1">
                        {company.successware_total.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Currently Imported</div>
                      <div className="text-2xl font-semibold mt-1">
                        {company.existing_count.toLocaleString()}
                      </div>
                      {company.successware_total > 0 && (
                        <div className="text-xs text-muted-foreground">
                          {Math.round((company.existing_count / company.successware_total) * 100)}% complete
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-muted-foreground">Missing</div>
                      <div className="text-2xl font-semibold mt-1 text-orange-500">
                        {company.missing_count.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {company.status === 'complete' && company.imported !== undefined && (
                    <Alert className="mt-4">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        Successfully imported {company.imported} customers
                        {company.failed && company.failed > 0 && ` (${company.failed} failed)`}
                      </AlertDescription>
                    </Alert>
                  )}

                  {company.status === 'error' && (
                    <Alert variant="destructive" className="mt-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{company.error_message}</AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Instructions */}
      {companies.length === 0 && !isAnalyzing && (
        <Card>
          <CardHeader>
            <CardTitle>How to Use This Tool</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="mt-0.5">1</Badge>
              <div>
                <div className="font-medium">Click "Analyze Gaps"</div>
                <div className="text-sm text-muted-foreground">
                  This will check each active company to see how many customers are in SuccessWare
                  vs. how many we have imported into our database
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="mt-0.5">2</Badge>
              <div>
                <div className="font-medium">Review the Results</div>
                <div className="text-sm text-muted-foreground">
                  You'll see a breakdown of each company showing SuccessWare totals, imported counts,
                  and missing records
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="mt-0.5">3</Badge>
              <div>
                <div className="font-medium">Import Missing Data</div>
                <div className="text-sm text-muted-foreground">
                  Use "Import All Missing" to import all gaps, or import company-by-company.
                  The system will only import records not already in the database using sw_service_account_id deduplication
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
